// Stub providers for DX Charts - minimal implementations for providers without backend equivalents

import type { MergedSymbolInfo } from './dx-trading-mappers'
import { getDisplayTick } from './symbol-price-precision'
import { resolveMappedShortSymbol } from './utils'
import { chartDebugLog } from '../debug/browser-logging'

export interface Instrument {
  symbol: string
  description?: string
  type?: string
  priceIncrements: number[]
  tradingHours?: string
  tradable?: boolean
}

export interface SymbolSuggestProvider {
  findInstrument(symbol: string): Promise<Instrument>
  searchInstruments(search: string): Promise<Instrument[]>
  onInstrumentChanged(symbol: string | undefined, chartId: string): void
}

export class StubSymbolSuggestProvider implements SymbolSuggestProvider {
  private readonly defaultPriceIncrements = [0.00001]
  private readonly defaultTradingHours = ''

  constructor(
    private symbolFullNames: Record<string, string>,
    private symbolShortNames: Record<string, string>,
    private mergedSymbols?: Record<string, MergedSymbolInfo>
  ) {}

  private resolveToShortName(symbol: string): string {
    return resolveMappedShortSymbol(symbol, this.symbolFullNames, this.symbolShortNames) || symbol
  }

  private getPriceIncrements(shortName: string): number[] {
    if (!this.mergedSymbols) {
      return this.defaultPriceIncrements
    }

    const merged = this.mergedSymbols[shortName]
    if (!merged) {
      return this.defaultPriceIncrements
    }

    const displayTick = getDisplayTick(merged.symbolInfo)
    return [displayTick]
  }

  // Use arrow methods so provider callbacks keep context when invoked unbound by chart internals.
  findInstrument = async (symbol: string): Promise<Instrument> => {
    const shortName = this.resolveToShortName(symbol)
    const fullName = this.symbolFullNames[shortName] || symbol
    const priceIncrements = this.getPriceIncrements(shortName)

    return {
      symbol: fullName,
      description: shortName,
      type: 'FOREX',
      priceIncrements,
      tradingHours: this.defaultTradingHours,
      tradable: true,
    }
  }

  searchInstruments = async (search: string): Promise<Instrument[]> => {
    return Object.entries(this.symbolShortNames)
      .filter(([_, short]) => short.toLowerCase().includes(search.toLowerCase()))
      .map(([full, short]) => {
        const priceIncrements = this.getPriceIncrements(short)
        return {
          symbol: full,
          description: short,
          type: 'FOREX',
          priceIncrements,
          tradingHours: this.defaultTradingHours,
          tradable: true,
        }
      })
  }

  onInstrumentChanged = (): void => {
    // no-op
  }
}

// Session type enumeration used by dxchart
export type SessionType = 'NO_TRADING' | 'PRE_MARKET' | 'REGULAR' | 'AFTER_MARKET'

// DXChart-compatible trading session shape: timestamps in milliseconds, type indicates session category
export interface TradingSession {
  readonly from: number
  readonly to: number
  readonly type: SessionType
}

export interface GenerateSessionOptions {
  tradingHours?: string
  symbol: string
}

export interface TradingSessionsProvider {
  generateSessions(from: number, to: number, options: GenerateSessionOptions): Promise<TradingSession[]>
  getTimeZone(options: GenerateSessionOptions): Promise<string>
}

/**
 * Tournament-aware trading sessions provider for forex (24x5) symbols.
 * Generates continuous REGULAR trading sessions spanning the tournament timeline.
 * Critical: Always extends to Date.now() + 28 days to ensure DXChart's market-state
 * model finds a matching session at the current time, preventing "Closest session not found" warnings.
 */
export class TournamentTradingSessionsProvider implements TradingSessionsProvider {
  private cachedSessions: TradingSession[] | null = null

  constructor(
    private startDate?: string,
    private endDate?: string,
  ) {
    // Pre-generate sessions eagerly to ensure they're available immediately
    // This avoids race conditions where DXChart queries sessions before async generateSessions completes
    this.generateSessionsSync()
  }

  private generateSessionsSync(): void {
    if (this.cachedSessions) return

    const now = Date.now()
    const DAY_MS = 24 * 60 * 60 * 1000

    // Parse tournament dates
    const parsedStart = this.startDate ? new Date(this.startDate).getTime() : undefined
    const parsedEnd = this.endDate ? new Date(this.endDate).getTime() : undefined
    const hasTournamentWindow = parsedStart !== undefined && parsedEnd !== undefined

    // Compute effective range
    const effectiveFrom = hasTournamentWindow ? parsedStart - 30 * DAY_MS : now - 365 * DAY_MS
    const effectiveTo = Math.max(
      hasTournamentWindow ? parsedEnd + 14 * DAY_MS : now + 365 * DAY_MS,
      now + 28 * DAY_MS // Always extend beyond now
    )

    const sessions: TradingSession[] = []
    const startDayMs = Math.floor(effectiveFrom / DAY_MS) * DAY_MS
    const endDayMs = Math.ceil(effectiveTo / DAY_MS) * DAY_MS

    for (let dayMs = startDayMs; dayMs < endDayMs; dayMs += DAY_MS) {
      sessions.push({
        from: dayMs,
        to: dayMs + DAY_MS,
        type: 'REGULAR',
      })
    }

    chartDebugLog('data', 'pre-generated sessions', { count: sessions.length })
    this.cachedSessions = sessions
  }

  /**
   * Generates forex trading sessions (24h REGULAR every day).
   * Always covers tournament window + buffers + extends to Date.now() + 28 days.
   * DXChart's market-state searches for sessions at Date.now(), so this must be included.
   */
  async generateSessions(
    from: number,
    to: number,
    options: GenerateSessionOptions,
  ): Promise<TradingSession[]> {
    // Return cached sessions if available (single cache for all calls)
    if (this.cachedSessions) {
      const fromNorm = Math.abs(from) < 1_000_000_000_000 ? from * 1000 : from
      const toNorm = Math.abs(to) < 1_000_000_000_000 ? to * 1000 : to
      const requestCoverageStart = this.cachedSessions[0]?.from
      const requestCoverageEnd = this.cachedSessions[this.cachedSessions.length - 1]?.to
      const coversRequest = requestCoverageStart <= fromNorm && requestCoverageEnd >= toNorm
      chartDebugLog('data', 'returning cached sessions', {
        count: this.cachedSessions.length,
        coversRequest,
      })
      return this.cachedSessions
    }

    // Normalize inputs: DXChart may pass seconds instead of milliseconds
    const normalize = (raw: number): number => {
      if (!Number.isFinite(raw)) return Date.now()
      return Math.abs(raw) < 1_000_000_000_000 ? raw * 1000 : raw
    }
    let fromMs = normalize(from)
    let toMs = normalize(to)
    if (fromMs > toMs) [fromMs, toMs] = [toMs, fromMs]

    // Parse tournament dates
    const parsedStart = this.startDate ? new Date(this.startDate).getTime() : undefined
    const parsedEnd = this.endDate ? new Date(this.endDate).getTime() : undefined
    const hasTournamentWindow = parsedStart !== undefined && parsedEnd !== undefined

    // Compute effective range: tournament window + buffers + MUST include Date.now()
    const effectiveFrom = hasTournamentWindow
      ? parsedStart - 30 * 24 * 60 * 60 * 1000
      : fromMs - 30 * 24 * 60 * 60 * 1000

    // Critical: always extend to at least Date.now() + 28 days
    // This ensures market-state finds a session at the current time
    const now = Date.now()
    const effectiveTo = Math.max(
      hasTournamentWindow ? parsedEnd + 14 * 24 * 60 * 60 * 1000 : toMs + 14 * 24 * 60 * 60 * 1000,
      now + 28 * 24 * 60 * 60 * 1000
    )

    chartDebugLog('data', 'generating sessions', {
      hasTournamentWindow,
      fromMs: new Date(effectiveFrom).toISOString(),
      toMs: new Date(effectiveTo).toISOString(),
    })

    // Generate one 24h REGULAR session per calendar day
    const sessions: TradingSession[] = []
    const startDayMs = Math.floor(effectiveFrom / (24 * 60 * 60 * 1000)) * (24 * 60 * 60 * 1000)
    const endDayMs = Math.ceil(effectiveTo / (24 * 60 * 60 * 1000)) * (24 * 60 * 60 * 1000)

    for (let dayMs = startDayMs; dayMs < endDayMs; dayMs += 24 * 60 * 60 * 1000) {
      sessions.push({
        from: dayMs,
        to: dayMs + 24 * 60 * 60 * 1000,
        type: 'REGULAR',
      })
    }

    chartDebugLog('data', 'generated sessions', {
      count: sessions.length,
      from: new Date(sessions[0]?.from).toISOString(),
      to: new Date(sessions[sessions.length - 1]?.to).toISOString(),
    })

    // Cache for subsequent calls
    this.cachedSessions = sessions
    return sessions
  }

  /**
   * Returns timezone for the given symbol and trading hours.
   * Tournament forex symbols use UTC.
   */
  async getTimeZone(options: GenerateSessionOptions): Promise<string> {
    return 'UTC'
  }
}

export class StubTradingSessionsProvider implements TradingSessionsProvider {
  async generateSessions(): Promise<TradingSession[]> {
    return []
  }

  async getTimeZone(): Promise<string> {
    return 'UTC'
  }
}

export interface ChartLayout {
  id: string
  name: string
  data: any
}

export interface ChartLayoutNamed {
  name: string
  data: any
}

export interface ChartLayoutWithId {
  id: string
  data: any
}

export interface ChartLayoutData {
  [layoutId: string]: ChartLayout
}

export interface LayoutProvider {
  createLayout(layout: ChartLayoutNamed): Promise<string>
  getLayouts(): Promise<ChartLayoutData>
  updateLayout(layoutData: ChartLayoutWithId): Promise<void>
  updateSelectedLayout(id: string): Promise<void>
  updateLayoutData(layoutData: ChartLayoutData): Promise<void>
  deleteLayout(id: string): Promise<void>
}

export class StubLayoutProvider implements LayoutProvider {
  private layouts: ChartLayoutData = {}
  private selectedLayoutId: string | null = null
  private layoutCounter = 0

  async createLayout(layout: ChartLayoutNamed): Promise<string> {
    const id = `layout-${++this.layoutCounter}`
    this.layouts[id] = { id, name: layout.name, data: layout.data }
    return id
  }

  async getLayouts(): Promise<ChartLayoutData> {
    return this.layouts
  }

  async updateLayout(layoutData: ChartLayoutWithId): Promise<void> {
    if (this.layouts[layoutData.id]) {
      this.layouts[layoutData.id].data = layoutData.data
    }
  }

  async updateSelectedLayout(id: string): Promise<void> {
    this.selectedLayoutId = id
  }

  async updateLayoutData(layoutData: ChartLayoutData): Promise<void> {
    this.layouts = layoutData
  }

  async deleteLayout(id: string): Promise<void> {
    delete this.layouts[id]
  }
}

export interface UserData {
  customPeriods?: any[]
  customColors?: any
  favoriteDrawings?: string[]
  recentDrawings?: string[]
  isSidebarExpanded?: boolean
  timeframePresets?: any[]
  positions?: any[]
}

export interface UserDataProvider {
  getUserData(): Promise<UserData | null>
  setUserData(userData: UserData): Promise<void>
}

export class StubUserDataProvider implements UserDataProvider {
  private userData: UserData | null = null

  async getUserData(): Promise<UserData | null> {
    return this.userData
  }

  async setUserData(userData: UserData): Promise<void> {
    this.userData = userData
  }
}

export interface EventsData {
  earnings?: any[]
  dividends?: any[]
  splits?: any[]
}

export interface EventsDataProvider {
  requestEventsData(symbol: string): Promise<EventsData>
}

export class StubEventsDataProvider implements EventsDataProvider {
  async requestEventsData(): Promise<EventsData> {
    return {}
  }
}

export interface NewsData {
  items?: any[]
}

export interface NewsDataProvider {
  requestNews(symbol: string, options?: { fromTime: number }): Promise<NewsData>
}

export class StubNewsDataProvider implements NewsDataProvider {
  async requestNews(): Promise<NewsData> {
    return {}
  }
}

export interface IndicatorTemplate {
  id: string
  name: string
  study: any
}

export interface IndicatorTemplateNamed {
  name: string
  study: any
}

export interface IndicatorTemplates {
  [templateId: string]: IndicatorTemplate
}

export interface IndicatorsTemplateProvider {
  createTemplate(template: IndicatorTemplateNamed): Promise<string>
  deleteTemplate(id: string): Promise<void>
  updateTemplate(template: IndicatorTemplate): Promise<void>
  getTemplates(): Promise<IndicatorTemplates>
}

export class StubIndicatorsTemplateProvider implements IndicatorsTemplateProvider {
  private templates: IndicatorTemplates = {}
  private templateCounter = 0

  async createTemplate(template: IndicatorTemplateNamed): Promise<string> {
    const id = `template-${++this.templateCounter}`
    this.templates[id] = { id, name: template.name, study: template.study }
    return id
  }

  async deleteTemplate(id: string): Promise<void> {
    delete this.templates[id]
  }

  async updateTemplate(template: IndicatorTemplate): Promise<void> {
    this.templates[template.id] = template
  }

  async getTemplates(): Promise<IndicatorTemplates> {
    return this.templates
  }
}

export interface ChartSharingProvider {
  uploadChartSnapshot(blob: Blob, options?: any): Promise<{ url: string }>
}

export class StubChartSharingProvider implements ChartSharingProvider {
  async uploadChartSnapshot(): Promise<{ url: string }> {
    throw new Error('Chart sharing is not supported')
  }
}

export interface CustomStudyDTO {
  id: string
  name: string
  code: string
}

export interface CustomStudiesProvider {
  getAllStudies(): Promise<CustomStudyDTO[]>
  getStudy(id: string): Promise<CustomStudyDTO | undefined>
  createStudy(study: CustomStudyDTO): Promise<void>
  updateStudy(study: CustomStudyDTO): Promise<void>
  deleteStudy(id: string): Promise<void>
}

export class StubCustomStudiesProvider implements CustomStudiesProvider {
  private studies: CustomStudyDTO[] = []

  async getAllStudies(): Promise<CustomStudyDTO[]> {
    return this.studies
  }

  async getStudy(id: string): Promise<CustomStudyDTO | undefined> {
    return this.studies.find((s) => s.id === id)
  }

  async createStudy(study: CustomStudyDTO): Promise<void> {
    this.studies.push(study)
  }

  async updateStudy(study: CustomStudyDTO): Promise<void> {
    const index = this.studies.findIndex((s) => s.id === study.id)
    if (index !== -1) {
      this.studies[index] = study
    }
  }

  async deleteStudy(id: string): Promise<void> {
    this.studies = this.studies.filter((s) => s.id !== id)
  }
}
